import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import mealPlannerService from '../services/mealPlannerService';
import { startOfWeek, endOfWeek, format } from 'date-fns';

// Async thunks
export const fetchUserPreferences = createAsyncThunk(
  'mealPlanner/fetchUserPreferences',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await mealPlannerService.getUserPreferences(userId);
      return response.preferences || {};
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveUserPreferences = createAsyncThunk(
  'mealPlanner/saveUserPreferences',
  async ({ userId, preferences }, { rejectWithValue }) => {
    try {
      const response = await mealPlannerService.saveUserPreferences(userId, preferences);
      return response.preferences;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const generateMealPlan = createAsyncThunk(
  'mealPlanner/generateMealPlan',
  async ({ userId, startDate, endDate, notes, missingMeals, fillMissingOnly }, { rejectWithValue }) => {
    try {
      console.log(`Generating meal plan from ${startDate} to ${endDate} with notes: ${notes || 'none'}`);
      
      // Debug log to verify payload structure
      const fullPayload = { userId, startDate, endDate, notes, missingMeals, fillMissingOnly };
      console.log('Redux generateMealPlan action called with:', JSON.stringify(fullPayload));
      
      if (missingMeals) {
        console.log('Missing meals data:', missingMeals);
      }
      const response = await mealPlannerService.generateMealPlan(
        userId, 
        startDate, 
        endDate, 
        notes, 
        missingMeals, 
        fillMissingOnly
      );
      return response.mealPlans;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMealPlans = createAsyncThunk(
  'mealPlanner/fetchMealPlans',
  async ({ userId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await mealPlannerService.getMealPlans(userId, startDate, endDate);
      return response.mealPlans;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const regenerateMeal = createAsyncThunk(
  'mealPlanner/regenerateMeal',
  async ({ userId, date, mealType, notes }, { rejectWithValue }) => {
    try {
      console.log(`Regenerating ${mealType} for ${date} with notes: ${notes || 'none'}`);
      const response = await mealPlannerService.regenerateMeal(userId, date, mealType, notes);
      return response.meal;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkShoppingList = createAsyncThunk(
  'mealPlanner/checkShoppingList',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const userId = auth.user?._id || auth.user?.id;
      
      if (!userId) {
        console.error('User not logged in or missing ID');
        return rejectWithValue('User not logged in');
      }
      
      console.log('Checking shopping list for user:', userId);
      
      // Use the service function
      const response = await mealPlannerService.checkShoppingList(userId);
      console.log('Shopping list check response:', response);
      
      return response;
    } catch (error) {
      console.error('Error checking shopping list:', error);
      return rejectWithValue(error.message || 'An error occurred');
    }
  }
);

export const fetchShoppingList = createAsyncThunk(
  'mealPlanner/fetchShoppingList',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const userId = auth.user?._id || auth.user?.id;
      
      if (!userId) {
        console.error('User not logged in or missing ID');
        return rejectWithValue('User not logged in');
      }
      
      console.log('Fetching shopping list for user:', userId);
      
      // Get current date range (start of week to end of week) if not provided
      let startDate, endDate;
      
      if (params.startDate && params.endDate) {
        startDate = params.startDate;
        endDate = params.endDate;
      } else if (params.startDate) {
        startDate = params.startDate;
        endDate = params.endDate || params.startDate;
      } else {
        // If no dates provided, we'll just check for any existing list
        // and the API will handle the response appropriately
        startDate = null;
        endDate = null;
      }
      
      if (startDate && endDate) {
        console.log(`Fetching shopping list from ${startDate} to ${endDate}`);
        if (params.forceGenerate) {
          console.log("Force generating a fresh shopping list (not using saved list)");
        }
      } else {
        console.log('Checking for existing shopping list');
      }
      
      // Use the service function with forceGenerate flag
      const response = await mealPlannerService.getShoppingList(
        userId, 
        startDate, 
        endDate, 
        params.forceGenerate === true // Ensure boolean value
      );
      console.log('Shopping list response:', response);
      
      // If forceGenerate is true, always use the requested date range
      if (params.forceGenerate) {
        response.dateRange = {
          start: startDate || "",
          end: endDate || startDate || ""
        };
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching shopping list:', error);
      return rejectWithValue(error.message || 'An error occurred');
    }
  }
);

export const saveShoppingList = createAsyncThunk(
  'mealPlanner/saveShoppingList',
  async (listData, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const userId = auth.user?._id || auth.user?.id;
      
      if (!userId) {
        console.error('User not logged in or missing ID');
        return rejectWithValue('User not logged in');
      }
      
      // Extract items and dateRange from state if not provided
      let { items, dateRange } = listData;
      
      if (!items || !dateRange) {
        const { mealPlanner } = getState();
        items = items || mealPlanner.shoppingList.items;
        dateRange = dateRange || mealPlanner.shoppingList.dateRange;
        
        if (!items || !dateRange) {
          return rejectWithValue('No shopping list data to save');
        }
      }
      
      console.log(`Saving shopping list for user: ${userId}`);
      console.log('Shopping list data:', { items, dateRange });
      
      // Use the service function
      const response = await mealPlannerService.saveShoppingList(userId, items, dateRange);
      console.log('Shopping list save response:', response);
      
      return {
        ...response,
        items,
        dateRange
      };
    } catch (error) {
      console.error('Error saving shopping list:', error);
      return rejectWithValue(error.message || 'An error occurred');
    }
  }
);

export const removeShoppingListItem = createAsyncThunk(
  'mealPlanner/removeShoppingListItem',
  async (itemData, { rejectWithValue, getState }) => {
    try {
      const { auth, mealPlanner } = getState();
      const userId = auth.user?._id || auth.user?.id;
      
      if (!userId) {
        console.error('User not logged in or missing ID');
        return rejectWithValue('User not logged in');
      }
      
      // Extract required item data
      const { item, listId } = itemData;
      
      if (!item || !item.category || !item.name) {
        return rejectWithValue('Invalid item data');
      }
      
      console.log(`Removing item ${item.name} from shopping list for user: ${userId}`);
      
      // Call API to remove the item
      const response = await mealPlannerService.removeShoppingListItem(userId, item, listId);
      console.log('Item removal response:', response);
      
      return {
        ...response,
        removedItem: item
      };
    } catch (error) {
      console.error('Error removing item from shopping list:', error);
      return rejectWithValue(error.message || 'An error occurred');
    }
  }
);

export const deleteShoppingList = createAsyncThunk(
  'mealPlanner/deleteShoppingList',
  async (listId, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const userId = auth.user?._id || auth.user?.id;
      
      if (!userId) {
        console.error('User not logged in or missing ID');
        return rejectWithValue('User not logged in');
      }
      
      if (!listId) {
        // Try to get listId from state
        const { mealPlanner } = getState();
        listId = mealPlanner.shoppingList.listId;
        
        if (!listId) {
          return rejectWithValue('No shopping list ID provided');
        }
      }
      
      console.log(`Deleting shopping list: ${listId} for user: ${userId}`);
      
      // Use the service function
      const response = await mealPlannerService.deleteShoppingList(userId, listId);
      console.log('Shopping list delete response:', response);
      
      return response;
    } catch (error) {
      console.error('Error deleting shopping list:', error);
      return rejectWithValue(error.message || 'An error occurred');
    }
  }
);

// Initial state
const initialState = {
  userPreferences: {
    dietaryRestrictions: [],
    healthGoals: "",
    cuisinePreferences: [],
    allergies: [],
    cookingSkillLevel: "intermediate",
    cookingTimePreference: 30,
    mealsPerDay: 3,
    portionSize: "medium",
    maxCaloriesPerMeal: 600
  },
  mealPlans: {},
  favoriteRecipes: [],
  shoppingList: {
    items: {},
    dateRange: { start: "", end: "" },
    listId: null,
    exists: false
  },
  status: "idle",
  error: null
};

// Slice
const mealPlannerSlice = createSlice({
  name: 'mealPlanner',
  initialState,
  reducers: {
    toggleFavoriteRecipe: (state, action) => {
      const recipe = action.payload;
      const index = state.favoriteRecipes.findIndex(r => r.id === recipe.id);
      
      if (index >= 0) {
        state.favoriteRecipes.splice(index, 1);
      } else {
        state.favoriteRecipes.push(recipe);
      }
    },
    clearMealPlans: (state) => {
      state.mealPlans = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    // Add custom action to update shopping list view without saving to DB
    updateShoppingListView: (state, action) => {
      console.log("Updating shopping list view with:", action.payload);
      state.shoppingList = {
        ...state.shoppingList,
        items: action.payload.items || {},
        dateRange: action.payload.dateRange || state.shoppingList.dateRange,
        // Don't update listId or exists flag as this is just a view update
      };
    },
    clearMealPreferences: (state) => {
      state.preferences = null;
    },
    clearShoppingList: (state) => {
      state.shoppingList = {
        items: {},
        dateRange: { start: "", end: "" },
        status: 'idle'
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch user preferences
      .addCase(fetchUserPreferences.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.userPreferences = action.payload;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Save user preferences
      .addCase(saveUserPreferences.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveUserPreferences.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.userPreferences = action.payload;
      })
      .addCase(saveUserPreferences.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Generate meal plan
      .addCase(generateMealPlan.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(generateMealPlan.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Update meal plans
        action.payload.forEach(meal => {
          if (!state.mealPlans[meal.date]) {
            state.mealPlans[meal.date] = {};
          }
          state.mealPlans[meal.date][meal.mealType] = meal;
        });
      })
      .addCase(generateMealPlan.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Fetch meal plans
      .addCase(fetchMealPlans.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMealPlans.fulfilled, (state, action) => {
        state.status = 'succeeded';
        
        // Debug: Log what we received from the API
        console.log('Meal plans received from API:', action.payload);
        
        // Update meal plans - handle empty response case
        if (!action.payload || action.payload.length === 0) {
          console.log('No meal plans returned from API');
          return;
        }
        
        // Process each meal plan
        action.payload.forEach(meal => {
          // Make sure meal has required properties
          if (!meal.date || !meal.mealType) {
            console.log('Skipping invalid meal plan:', meal);
            return;
          }
          
          // Create nested structure if it doesn't exist
          if (!state.mealPlans[meal.date]) {
            state.mealPlans[meal.date] = {};
          }
          
          // Store meal by type
          state.mealPlans[meal.date][meal.mealType] = meal;
          console.log(`Added meal for ${meal.date}, type: ${meal.mealType}`);
        });
      })
      .addCase(fetchMealPlans.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Regenerate meal
      .addCase(regenerateMeal.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(regenerateMeal.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const meal = action.payload;
        // Update specific meal
        if (state.mealPlans[meal.date]) {
          state.mealPlans[meal.date][meal.mealType] = meal;
        }
      })
      .addCase(regenerateMeal.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Shopping List Operations
      .addCase(updateShoppingListView, (state, action) => {
        state.shoppingList = { ...state.shoppingList, ...action.payload };
      })
      .addCase(fetchShoppingList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchShoppingList.fulfilled, (state, action) => {
        state.status = 'idle';
        // If no payload, don't update
        if (!action.payload) {
          console.error('Empty payload in fetchShoppingList.fulfilled');
          return;
        }
        
        console.log('API response in fetchShoppingList.fulfilled:', action.payload);
        
        // Update shopping list with the response data
        state.shoppingList = {
          items: action.payload.items || {},
          // If forceGenerate is true, always use the provided dateRange
          dateRange: action.payload.forceGenerate 
            ? action.payload.dateRange 
            : (action.payload.dateRange || { start: '', end: '' }),
          listId: action.payload.listId || null,
          exists: action.payload.forceGenerate ? false : !!action.payload.fromSaved,
          message: action.payload.message || null
        };
        
        console.log('Updated shopping list in redux store:', state.shoppingList);
      })
      .addCase(fetchShoppingList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch shopping list';
        console.error('Shopping list fetch failed:', action.payload);
      })
      .addCase(checkShoppingList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(checkShoppingList.fulfilled, (state, action) => {
        state.status = 'idle';
        // Update the shopping list exists flag and dateRange if it exists
        if (action.payload.exists) {
          state.shoppingList = {
            ...state.shoppingList,
            exists: true,
            dateRange: action.payload.dateRange,
            listId: action.payload.listId
          };
        } else {
          state.shoppingList.exists = false;
        }
      })
      .addCase(checkShoppingList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to check shopping list';
      })
      .addCase(saveShoppingList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveShoppingList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.shoppingList = {
          ...state.shoppingList,
          saved: true,
          listId: action.payload.listId,
          items: action.payload.items || state.shoppingList.items,
          dateRange: action.payload.dateRange || state.shoppingList.dateRange
        };
      })
      .addCase(saveShoppingList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to save shopping list';
      })
      .addCase(removeShoppingListItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(removeShoppingListItem.fulfilled, (state, action) => {
        state.status = 'idle';
        // Update the items with the updated list returned from the API
        if (action.payload.items) {
          state.shoppingList = {
            ...state.shoppingList,
            items: action.payload.items
          };
        }
      })
      .addCase(removeShoppingListItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to remove item from shopping list';
      })
      .addCase(deleteShoppingList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteShoppingList.fulfilled, (state) => {
        state.status = 'idle';
        // Completely reset the shopping list state
        state.shoppingList = {
          items: {},
          dateRange: { start: "", end: "" },
          listId: null,
          exists: false,
          saved: false
        };
        console.log('Shopping list state cleared after successful deletion');
      })
      .addCase(deleteShoppingList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to delete shopping list';
      });
  }
});

export const { toggleFavoriteRecipe, clearMealPlans, clearError, updateShoppingListView } = mealPlannerSlice.actions;

export default mealPlannerSlice.reducer; 